// Эндпоинты официального контура выплат: периоды 15 → 15, строки, транши, перенос остатка.
//
// Формы — зеркало contracts/openapi/bark-core.v1.yaml. Контракт правит только сессия ядра;
// поле, придуманное на фронте, даёт зелёную сборку и пустой экран у человека.
//
// Пять особенностей, из-за которых типы выглядят именно так.
//
// 1. **Границу периода и последний его день сервер отдаёт РАЗНЫМИ полями.** `date_to` —
//    граница исключительно: 15 число принадлежит уже следующему периоду. Показывать человеку
//    надо `last_day` (14 августа), потому что в книгах заказчика стоит именно он. Оба поля
//    лежат рядом ровно поэтому, и вычитать день на клиенте не нужно — `title` уже собран.
// 2. **Связанный месяц ведомости приходит числами `payroll_year` и `payroll_month`.** Смещение
//    задаётся настройкой `official_period_offset_months`, а не константой, и считать его в
//    браузере значит однажды молча разойтись с сервером — в том самом месте, где спецификация
//    уже чуть не потеряла деньги (1.4-бис, оговорка про «Офис Сургут Июнь»).
// 3. **Три денежные величины строки едут всегда вместе: `paid`, `planned`, `balance`.**
//    Заведённый, но не выплаченный транш остаток НЕ уменьшает. Убрав `planned` из показа, мы
//    получим экран, где деньги «пропали»; показав только его — экран, где их «выдали дважды».
// 4. **Процент удержания — строка, а не число.** JSON-число в браузере становится приближением
//    с плавающей точкой, а на этом проценте решает половина копейки: Тарасов А.В., 55 252,26 ×
//    25 % = 13 813,065, в файле заказчика 13 813,07.
// 5. **Суммы — целые копейки.** Ни одной дробной денежной величины в контуре нет.

import type { FileResponse } from '@/shared/api/client';
import { request, requestFile, type RequestOptions } from '@/shared/api/client';
import { queryString } from '@/shared/api/query';
import type { ListResponse } from '@/shared/api/types';

// ── Замороженные наборы кодов ─────────────────────────────────────────────────────────────

/**
 * Состояния строки. Справочник целиком приезжает вместе с сеткой (`OfficialGridOut.states`), и
 * рисовать надо ЕГО: этот набор нужен только для отбора в адресе и для проверок.
 */
export const ROW_STATES = [
  'can_pay',
  'paid',
  'check_details',
  'via_cashbox',
  'check_with_responsible',
] as const;
export type RowState = (typeof ROW_STATES)[number];

export function isRowState(value: string): value is RowState {
  return (ROW_STATES as readonly string[]).includes(value);
}

/** Виды транша. `carry_over` заводит ТОЛЬКО закрытие периода, руками его не создать. */
export const TRANCHE_KINDS = ['main', 'weekly', 'carry_over'] as const;
export type TrancheKind = (typeof TRANCHE_KINDS)[number];

/** Виды, которые заводит человек. Перенос сюда не входит намеренно. */
export const MANUAL_TRANCHE_KINDS = ['main', 'weekly'] as const;
export type ManualTrancheKind = (typeof MANUAL_TRANCHE_KINDS)[number];

/** Признаки строки для панели проверки. Плоские коды: веб не собирает их из чисел заново. */
export const ROW_FLAGS = ['carried_in', 'overpaid', 'unpaid_tranches', 'carry_refused'] as const;
export type RowFlag = (typeof ROW_FLAGS)[number];

/** Вердикт сопоставления фамилии из книги заказчика. */
export const MATCH_VERDICTS = ['exact', 'fuzzy', 'ambiguous', 'not_found'] as const;
export type MatchVerdict = (typeof MATCH_VERDICTS)[number];

// ── Итоги и период ────────────────────────────────────────────────────────────────────────

/** Итоги периода. Ноль здесь означает «посчитано и ноль», а не «не знаем». */
export interface OfficialTotalsOut {
  /** Строк в периоде, включая перенесённые из прошлого. */
  rows_count: number;
  carried_in_rows: number;
  gross_official: number;
  writ_amount: number;
  payable: number;
  /** Выплачено: транши с проставленной датой. */
  paid: number;
  /** Заведено траншами, но не выплачено. Остаток НЕ уменьшает. */
  planned: number;
  carried_out: number;
  balance: number;
  unsettled_rows: number;
  unsettled_total: number;
  overpaid_rows: number;
  /**
   * Переплачено, ПОЛОЖИТЕЛЬНЫМ числом. Складывать с непогашенным нельзя: их слипание в одно
   * число и делает третью величину нечитаемой — у заказчика в книге стоит именно слипшееся
   * 528 842,95 вместо 543 970,06 непогашенного и 15 127,11 переплат.
   */
  overpaid_total: number;
  unpaid_tranches: number;
  unpaid_total: number;
  refused_rows: number;
}

/** Официальный период с 15 по 15 число. */
export interface OfficialPeriodOut {
  id: string;
  /** Первый день периода, включительно. */
  date_from: string;
  /** Граница периода, ИСКЛЮЧИТЕЛЬНО: 15 число попадает уже в следующий период. */
  date_to: string;
  /** Последний день периода, включительно — для показа. */
  last_day: string;
  /** Период словами: «15.07.2026 — 14.08.2026». Собран сервером по `last_day`. */
  title: string;
  payroll_year: number;
  payroll_month: number;
  status: string;
  status_title: string;
  closed_at: string | null;
  closed_by_name: string | null;
  version: number;
  totals: OfficialTotalsOut;
  can_edit: boolean;
  blocked_reason: string | null;
}

// ── Строка и транши ───────────────────────────────────────────────────────────────────────

/** Транш: заведённая сумма и, отдельно, факт её выдачи. */
export interface TrancheOut {
  id: string;
  official_row_id: string;
  kind: string;
  week_no: number | null;
  /** Название для человека: «Основная выплата», «Неделя 3», «Сторно · Неделя 3». */
  label: string;
  /** Сумма в копейках; у сторно отрицательная. */
  amount: number;
  paid_at: string | null;
  /** Выдан ли. Отделено от суммы намеренно: заведённый и выплаченный — разные состояния денег. */
  is_paid: boolean;
  is_reversed: boolean;
  /** Заполнено у самой обратной записи. */
  reversal_of_id: string | null;
  reversal_basis: string | null;
  version: number;
}

/** Строка периода: обязательство перед человеком и его погашение траншами. */
export interface OfficialRowOut {
  id: string;
  official_period_id: string;
  employee_id: string;
  employee_name: string;
  employee_status: string | null;
  /** Полная официальная сумма до удержания, копейки. */
  gross_official: number;
  /** Процент удержания СТРОКОЙ, например «70.00». */
  writ_percent: string | null;
  /** Удержано: round(полная × процент ÷ 100), ROUND_HALF_UP. */
  writ_amount: number;
  /** К выдаче: полная минус удержание. */
  payable: number;
  /** Выплачено траншами с датой. */
  paid: number;
  /** Заведено траншами без даты. Остаток НЕ уменьшает. */
  planned: number;
  /** Ушло переносом в следующий период. */
  carried_out: number;
  /** Остаток. Отрицательный — это ПЕРЕПЛАТА, а не ноль и не ошибка. */
  balance: number;
  state: string;
  state_title: string;
  note: string | null;
  is_carried_in: boolean;
  carried_from_period_id: string | null;
  carried_from_title: string | null;
  carry_refused_basis: string | null;
  flags: string[];
  /** Одной фразой, почему строка в панели проверки. */
  review_reason: string | null;
  tranche_count: number;
  version: number;
  tranches: TrancheOut[];
}

/** Что не легло и ПОЧЕМУ. Поимённо: молча проглоченная строка не восстанавливается ничем. */
export interface SkippedOut {
  /** Может отсутствовать: в контракте это поле не обязательное. */
  id?: string | null;
  label: string;
  reason: string;
}

export interface StateOptionOut {
  code: string;
  title: string;
}

/**
 * Панель проверки над сеткой периода.
 *
 * `nothing_to_check` существует отдельно от нулей намеренно: ноль в счётчике означает
 * «проверено, всё чисто», и это надо сказать словом. Молчание читается как «не проверяли».
 */
export interface ReviewPanelOut {
  carried_in_rows: number;
  carried_in_total: number;
  overpaid_rows: number;
  /** Положительным числом. */
  overpaid_total: number;
  unpaid_tranches: number;
  unpaid_total: number;
  refused_rows: number;
  refused_total: number;
  /** Строк в состоянии «уточнить реквизиты». */
  no_details_rows: number;
  nothing_to_check: boolean;
  /** Одна фраза для кнопки и заголовка панели. */
  summary: string;
}

/**
 * Всё, что нужно экрану периода, одним ответом.
 *
 * Одним, а не тремя: счётчики панели проверки, посчитанные отдельным запросом, разойдутся с
 * сеткой на любой параллельной правке — панель скажет «три переплаты», а в сетке их окажется
 * две, и человек перестанет верить обеим.
 */
export interface OfficialGridOut {
  period: OfficialPeriodOut;
  rows: OfficialRowOut[];
  /** Номера недельных колонок; берутся из настройки. */
  weeks: number[];
  states: StateOptionOut[];
  /** Строк показано после отбора. */
  rows_shown: number;
  /** Строк в периоде всего. Отличие от `rows_shown` разводит «пусто» и «отбор ничего не нашёл». */
  rows_total: number;
  review: ReviewPanelOut;
  can_edit: boolean;
  blocked_reason: string | null;
}

/** Карточка человека в контуре: вся его официальная история. */
export interface EmployeeCircuitOut {
  employee_id: string;
  employee_name: string;
  rows: OfficialRowOut[];
  totals: OfficialTotalsOut;
  writ_orders: WritOrderOut[];
}

// ── Исполнительные документы ──────────────────────────────────────────────────────────────

export interface WritOrderOut {
  id: string;
  employee_id: string;
  employee_name: string;
  /** Процент строкой. */
  percent: string;
  document_number: string | null;
  basis: string;
  date_from: string;
  date_to: string | null;
  is_active: boolean;
}

export interface WritOrderIn {
  employee_id: string;
  percent: string;
  basis: string;
  date_from: string;
  date_to?: string | null;
  document_number?: string | null;
}

// ── Закрытие и переоткрытие ───────────────────────────────────────────────────────────────

/** Что произойдёт при закрытии. Числа называются ДО нажатия. */
export interface ClosePreviewOut {
  rows_total: number;
  /** Погашено полностью. */
  settled_rows: number;
  carry_rows: number;
  /** На какую сумму, копейки. */
  carry_total: number;
  carry_people: number;
  refused_rows: number;
  /** Отказались переносить — эти деньги в новый период не уйдут. */
  refused_total: number;
  overpaid_rows: number;
  /** Переплаты не переносятся, положительным числом. */
  overpaid_total: number;
  unpaid_tranches: number;
  unpaid_total: number;
  /** Пусто — следующий период заведётся сам. */
  next_period_id: string | null;
  next_period_title: string;
  /** Пусто — закрывать можно. */
  blockers: string[];
  summary: string;
}

export interface CloseOut {
  period: OfficialPeriodOut;
  carried_rows: number;
  carried_total: number;
  carried_people: number;
  next_period_id: string;
  next_period_title: string;
}

/** Что произойдёт при переоткрытии: перенос откатится сторно, а не удалением. */
export interface ReopenPreviewOut {
  carried_rows: number;
  carried_total: number;
  /** По скольким перенесённым строкам уже выплачивали. */
  paid_rows: number;
  paid_total: number;
  /** Кому именно — поимённо, чтобы отказ был понятен. */
  paid_people: string[];
  blockers: string[];
  summary: string;
}

export interface ReopenOut {
  period: OfficialPeriodOut;
  reverted_rows: number;
  reverted_total: number;
}

// ── Загрузка сумм ─────────────────────────────────────────────────────────────────────────

/** Строка вставленного куска: как есть, без разбора на клиенте. */
export interface MatchLineIn {
  name: string;
  /** Сумма как её вставили: «51 869,40», «51869.4», «51 869». Разбирает сервер. */
  amount: string;
}

/** Человек, на которого может лечь строка из книги. */
export interface EmployeeCandidateOut {
  employee_id: string;
  employee_name: string;
  status: string | null;
  /** Состояние занятости словами («На вахте»): код в `status` человеку не читается. */
  status_title?: string | null;
}

/** Одна строка из книги заказчика и что мы про неё поняли. */
export interface MatchRowOut {
  /** Номер строки во вставленном куске, с единицы. */
  line: number;
  /** Как фамилия написана в книге. */
  raw_name: string;
  /** Сумма в копейках; пусто — разобрать не вышло. */
  amount: number | null;
  amount_error: string | null;
  verdict: string;
  employee_id: string | null;
  employee_name: string | null;
  /** Чем совпадение отличается от точного. */
  note: string | null;
  /** Заполнено при ambiguous: из кого выбирать. */
  candidates?: EmployeeCandidateOut[];
}

/** Отчёт сопоставления. Ничего не записано: решение принимает человек, увидев итог. */
export interface MatchOut {
  rows: MatchRowOut[];
  exact_count: number;
  fuzzy_count: number;
  ambiguous_count: number;
  not_found_count: number;
  /** Сколько строк ляжет без вмешательства. */
  ready_count: number;
  /** На какую сумму, копейки. */
  ready_total: number;
  summary: string;
}

export interface ImportRowIn {
  employee_id: string;
  gross_official: number;
}

export interface RowIn {
  employee_id: string;
  /** Полная официальная сумма в копейках. */
  gross_official: number;
  writ_percent?: string | null;
  /** Состояние строки; пусто — не менять. */
  state?: string | null;
  note?: string | null;
}

export interface RowsLoadedOut {
  loaded: number;
  skipped: SkippedOut[];
  grid: OfficialGridOut;
}

// ── Массовые действия ─────────────────────────────────────────────────────────────────────

export interface BulkStateOut {
  changed: number;
  skipped: SkippedOut[];
  grid: OfficialGridOut;
}

export interface BulkPayOut {
  /** Сколько траншей проведено ЭТИМ действием. */
  paid: number;
  /**
   * Сколько уже были отмечены раньше. Проговаривается словами: сервер проводит не всё, что
   * прислали, и молчание об этом заставит нажать второй раз.
   */
  already_paid: number;
  skipped: SkippedOut[];
  grid: OfficialGridOut;
}

// ── Запросы ───────────────────────────────────────────────────────────────────────────────

export function fetchPeriods(
  params: { year?: number } = {},
  options?: RequestOptions,
): Promise<ListResponse<OfficialPeriodOut>> {
  return request<ListResponse<OfficialPeriodOut>>(
    `/api/v1/official/periods${queryString(params)}`,
    options,
  );
}

export function fetchGrid(
  periodId: string,
  params: { state?: string; q?: string } = {},
  options?: RequestOptions,
): Promise<OfficialGridOut> {
  return request<OfficialGridOut>(
    `/api/v1/official/periods/${periodId}/grid${queryString(params)}`,
    options,
  );
}

export function fetchClosePreview(
  periodId: string,
  options?: RequestOptions,
): Promise<ClosePreviewOut> {
  return request<ClosePreviewOut>(`/api/v1/official/periods/${periodId}/close-preview`, options);
}

export function fetchReopenPreview(
  periodId: string,
  options?: RequestOptions,
): Promise<ReopenPreviewOut> {
  return request<ReopenPreviewOut>(`/api/v1/official/periods/${periodId}/reopen-preview`, options);
}

export function fetchEmployeeCircuit(
  employeeId: string,
  options?: RequestOptions,
): Promise<EmployeeCircuitOut> {
  return request<EmployeeCircuitOut>(`/api/v1/official/employees/${employeeId}`, options);
}

export function fetchWritOrders(
  params: { employee_id?: string; active_only?: boolean } = {},
  options?: RequestOptions,
): Promise<ListResponse<WritOrderOut>> {
  return request<ListResponse<WritOrderOut>>(
    `/api/v1/official/writ-orders${queryString(params)}`,
    options,
  );
}

// ── Действия ──────────────────────────────────────────────────────────────────────────────

export function createPeriod(body: { year: number; month: number }): Promise<OfficialPeriodOut> {
  return request<OfficialPeriodOut>('/api/v1/official/periods', { method: 'POST', body });
}

export function upsertRows(periodId: string, rows: RowIn[]): Promise<RowsLoadedOut> {
  return request<RowsLoadedOut>(`/api/v1/official/periods/${periodId}/rows`, {
    method: 'PUT',
    body: { rows },
  });
}

/** Сопоставление фамилий. НИЧЕГО не записывает: решение принимает человек, увидев отчёт. */
export function matchNames(periodId: string, lines: MatchLineIn[]): Promise<MatchOut> {
  return request<MatchOut>(`/api/v1/official/periods/${periodId}/rows/match`, {
    method: 'POST',
    body: { lines },
  });
}

export function importRows(periodId: string, rows: ImportRowIn[]): Promise<RowsLoadedOut> {
  return request<RowsLoadedOut>(`/api/v1/official/periods/${periodId}/rows/import`, {
    method: 'POST',
    body: { rows },
  });
}

export function applyWrits(periodId: string): Promise<OfficialGridOut> {
  return request<OfficialGridOut>(`/api/v1/official/periods/${periodId}/apply-writs`, {
    method: 'POST',
  });
}

export function setRowWrit(
  rowId: string,
  percent: string | null,
  version: number,
): Promise<OfficialRowOut> {
  return request<OfficialRowOut>(`/api/v1/official/rows/${rowId}/writ`, {
    method: 'PUT',
    body: { percent, version },
  });
}

export function setRowState(
  rowId: string,
  state: string,
  version: number,
): Promise<OfficialRowOut> {
  return request<OfficialRowOut>(`/api/v1/official/rows/${rowId}/state`, {
    method: 'PUT',
    body: { state, version },
  });
}

export function setStates(
  periodId: string,
  rowIds: string[],
  state: string,
): Promise<BulkStateOut> {
  return request<BulkStateOut>(`/api/v1/official/periods/${periodId}/states`, {
    method: 'POST',
    body: { row_ids: rowIds, state },
  });
}

export interface TrancheIn {
  kind: ManualTrancheKind;
  /** Сумма в копейках, строго положительная. */
  amount: number;
  week_no?: number | null;
  /** Момент выплаты; пусто — транш заведён, но ещё не выдан. */
  paid_at?: string | null;
}

export function addTranche(
  rowId: string,
  body: TrancheIn,
  idempotencyKey: string,
): Promise<TrancheOut> {
  return request<TrancheOut>(`/api/v1/official/rows/${rowId}/tranches`, {
    method: 'POST',
    body,
    idempotencyKey,
  });
}

export function payTranche(trancheId: string, paidAt?: string | null): Promise<TrancheOut> {
  return request<TrancheOut>(`/api/v1/official/tranches/${trancheId}/pay`, {
    method: 'POST',
    body: { paid_at: paidAt ?? null },
  });
}

export function payTranches(
  periodId: string,
  trancheIds: string[],
  paidAt?: string | null,
): Promise<BulkPayOut> {
  return request<BulkPayOut>(`/api/v1/official/periods/${periodId}/tranches/pay`, {
    method: 'POST',
    body: { tranche_ids: trancheIds, paid_at: paidAt ?? null },
  });
}

/** Сторно транша: руководство и администратор. Правки денежной записи не существует. */
export function reverseTranche(trancheId: string, basis: string): Promise<TrancheOut> {
  return request<TrancheOut>(`/api/v1/official/tranches/${trancheId}/reverse`, {
    method: 'POST',
    body: { basis },
  });
}

export function refuseCarry(
  rowId: string,
  basis: string,
  version: number,
): Promise<OfficialRowOut> {
  return request<OfficialRowOut>(`/api/v1/official/rows/${rowId}/refuse-carry`, {
    method: 'POST',
    body: { basis, version },
  });
}

export function closePeriod(periodId: string, version: number): Promise<CloseOut> {
  return request<CloseOut>(`/api/v1/official/periods/${periodId}/close`, {
    method: 'POST',
    body: { version },
  });
}

export function reopenPeriod(periodId: string, version: number, basis: string): Promise<ReopenOut> {
  return request<ReopenOut>(`/api/v1/official/periods/${periodId}/reopen`, {
    method: 'POST',
    body: { version, basis },
  });
}

export function createWritOrder(body: WritOrderIn): Promise<WritOrderOut> {
  return request<WritOrderOut>('/api/v1/official/writ-orders', { method: 'POST', body });
}

/**
 * Выгрузка периода в Excel: сверка с книгой заказчика.
 *
 * Заказчик ведёт официальную часть в своей книге и будет вести ещё долго — по ней он сверяется
 * с бухгалтерией. Без выгрузки сверить период можно только переписыванием двухсот строк руками,
 * а расхождения появляются именно на переписывании.
 */
export function exportOfficialPeriod(periodId: string, title: string): Promise<FileResponse> {
  return requestFile(
    `/api/v1/official/periods/${periodId}/export`,
    {},
    `Официальный контур ${title}.xlsx`,
  );
}
